import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError} from 'rxjs/operators';

import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoginService } from '../authentication.service';
import { DiagnosticsService } from '../diagnostics.service';
import { signalCrossTabLogout } from '../cross-tab-signal';
import { environment } from 'src/environments/environment';
import { ApiError } from './api-error';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private redirectingToServerDown = false;

    constructor(
        private authenticationService: LoginService,
        private router: Router,
        private snackBar: MatSnackBar,
        private diagnostics: DiagnosticsService
    ) { }

    private navigateToServerDown(): void {
        if (this.redirectingToServerDown) {
            return;
        }
        this.redirectingToServerDown = true;
        this.authenticationService.removeLocalStorage();
        this.router.navigate(['/server-down']).then(() => {
            this.redirectingToServerDown = false;
        });
    }

    private readonly FALLBACK_MESSAGES: Record<number, string> = {
        400: "Invalid request. Please check your input and try again.",
        403: "You don't have permission to perform this action.",
        404: "The requested resource was not found.",
        409: "A conflict occurred. Please refresh and try again.",
        422: "The data provided is invalid. Please check and try again.",
        429: "Too many requests. Please wait a moment and try again.",
        500: "Something went wrong on our end. Please try again later.",
        503: "Service is temporarily unavailable. Please try again later.",
    };

    private sanitizeErrorMessage(raw: string, status: number): string {
        if (!raw) {
            return this.FALLBACK_MESSAGES[status] ?? "Something went wrong. Please try again.";
        }

        // Detect technical/internal error patterns that should never reach the user
        const technicalPatterns = [
            /collection scan/i,
            /durationMillis/i,
            /caused by ::/i,
            /MongoError/i,
            /BSON/i,
            /E11000/i,               // MongoDB duplicate key
            /index.*failed/i,
            /cannot index/i,
            /parallel arrays/i,
            /aggregate/i,
            /\bcast\b.*failed/i,     // Mongoose cast errors
            /ObjectId/i,
            /ValidationError:/i,
            /TypeError:/i,
            /ReferenceError:/i,
            /SyntaxError:/i,
            /at\s+\w+\s*\(/i,        // Stack trace lines
            /node_modules/i,
            /\.js:\d+:\d+/i,         // File:line:col patterns
            /ECONNREFUSED/i,
            /ETIMEDOUT/i,
            /ENOTFOUND/i,
        ];

        if (technicalPatterns.some((p) => p.test(raw))) {
            if (!environment.production) {
                console.error("[API Error]", raw);
            }
            return this.FALLBACK_MESSAGES[status] ?? "Something went wrong. Please try again.";
        }

        if (raw.length > 200) {
            if (!environment.production) {
                console.error("[API Error]", raw);
            }
            return this.FALLBACK_MESSAGES[status] ?? "Something went wrong. Please try again.";
        }

        return raw;
    }

    /**
     * Keeps the user-facing message exactly as it was while carrying the status forward, so a
     * caller can branch on `err.status` instead of string-matching a sanitised message.
     */
    private toApiError(message: string, err: HttpErrorResponse): ApiError {
        return new ApiError(message, {
            status: err.status,
            serverName: err.error?.name,
            data: err.error?.data,
            body: err.error,
        });
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const isAppApi = request.url.startsWith(environment.apiUrl);

        return next.handle(request).pipe(
            catchError((err: HttpErrorResponse) => {
                let errorMessage: string = "An unknown error occurred.";

                // Record failed app-API calls so bug reports carry the last failures.
                if (isAppApi) {
                    this.diagnostics.recordApiError(request.method, request.url, err);
                }

                if (err.status === 0) {
                    // Network-level failure. Never retried automatically; the caller re-submits.
                    if (!navigator.onLine) {
                        errorMessage = "No internet connection. Please check your network and try again.";
                    } else {
                        errorMessage = "Unable to connect to the server. Please check your internet connection.";
                    }
                    return throwError(() => this.toApiError(errorMessage, err));
                }
                else if (err.status === 401 && isAppApi) {
                    const serverMessage = err.error?.message || '';
                    const errorName = err.error?.name || '';

                    // Facebook/Meta token expiry — not a user session issue.
                    // Let the calling component handle the reconnection prompt.
                    if (errorName === 'NotAuthenticated' && request.url.includes('/facebook_leads')) {
                        return throwError(() =>
                            this.toApiError(serverMessage || 'Your Meta access token has expired. Please reconnect your account.', err),
                        );
                    }

                    // Account-state rejection: the API flags suspended/deleted/archived accounts
                    // via data.reason, distinct from bad credentials. The JWT may still be unexpired,
                    // so the token-expiry check below would NOT log out and the portal would hang on
                    // a dead session. Skipped on the login call itself, which has no session to end.
                    const accountStateReason = err.error?.data?.reason;
                    const isAccountStateRejection = ['account_suspended', 'account_deleted', 'account_archived'].includes(accountStateReason);
                    if (isAccountStateRejection && !request.url.includes('/authentication')) {
                        this.authenticationService.removeLocalStorage();
                        signalCrossTabLogout();
                        const stateMessage = serverMessage || 'Your account is no longer active. Please contact your administrator.';
                        this.snackBar.open(stateMessage, 'close', { duration: 10000, verticalPosition: 'top' });
                        this.router.navigate(['/login']);
                        return throwError(() => this.toApiError(stateMessage, err));
                    }

                    const isSessionReplaced = serverMessage.includes('session has been replaced')
                        || serverMessage.includes('session replaced')
                        || serverMessage.includes('Another browser session');

                    if (isSessionReplaced) {
                        // Another browser session logged in — force logout immediately
                        this.authenticationService.removeLocalStorage();
                        signalCrossTabLogout();
                        this.snackBar.open(
                            'Session ended. You logged in on another browser.',
                            'close',
                            { duration: 8000, verticalPosition: 'top' }
                        );
                        this.router.navigate(['/login']);
                        errorMessage = serverMessage;
                    } else {
                        // Force logout when the token is expired/missing, or when the server
                        // explicitly rejected it.
                        const currentUser = localStorage.getItem("currentUser");
                        let shouldLogout = false;

                        // "NotAuthenticated" with JWT errors means the token itself is invalid
                        // (rotated secret, wrong environment, tampered) — always logout
                        const isTokenRejected = errorName === 'NotAuthenticated'
                            && (serverMessage.includes('invalid signature')
                                || serverMessage.includes('jwt malformed')
                                || serverMessage.includes('jwt expired')
                                || serverMessage.includes('invalid token'));

                        if (isTokenRejected) {
                            shouldLogout = true;
                        } else if (currentUser) {
                            try {
                                const { token } = JSON.parse(currentUser);
                                if (token) {
                                    const payload = JSON.parse(atob(token.split(".")[1]));
                                    shouldLogout = payload.exp * 1000 < Date.now();
                                } else {
                                    shouldLogout = true;
                                }
                            } catch {
                                shouldLogout = true;
                            }
                        } else {
                            shouldLogout = true;
                        }

                        if (shouldLogout) {
                            this.authenticationService.removeLocalStorage();
                            signalCrossTabLogout();
                            this.router.navigate(['/login']);
                        }
                        errorMessage = serverMessage || "Access denied";
                    }
                }
                else if (err.status === 502 && isAppApi) {
                    this.navigateToServerDown();
                    errorMessage = "Server is temporarily unavailable. Please try again later.";
                }
                else if (err.error instanceof ErrorEvent) {
                    errorMessage = err.error.message || "A client-side error occurred.";
                }
                else {
                    // Detect HTML responses (API returned frontend page instead of JSON).
                    // This happens when the API server is down and Nginx serves index.html.
                    const body = err.error;
                    const htmlContent = typeof body === 'string' ? body : body?.text;
                    if (typeof htmlContent === 'string' && htmlContent.trimStart().toLowerCase().startsWith('<!doctype')) {
                        if (isAppApi) {
                            this.navigateToServerDown();
                        }
                        errorMessage = "Server is temporarily unavailable. Please try again later.";
                    } else {
                        const rawMessage: string = body?.message || '';
                        errorMessage = this.sanitizeErrorMessage(rawMessage, err.status);
                    }
                }

                return throwError(() => this.toApiError(errorMessage, err));
            })
        );
    }
}
